import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  GoogleAuthProvider, 
  signInWithPopup,
  onAuthStateChanged,
  signOut,
  User
} from 'firebase/auth';
import { auth } from '../config/firebase';
import api from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    // Add scopes if needed
    provider.addScope('profile');
    provider.addScope('email');
    
    try {
      // console.log('Starting Google sign-in process...');
      const result = await signInWithPopup(auth, provider);
      // console.log('Google sign-in successful');
      
      // After successful sign-in, you might want to create/update user in your backend
      const token = await result.user.getIdToken();
      // console.log('Got ID token');
      
      // Store the token in localStorage
      localStorage.setItem('token', token);
      
      try {
        // console.log('Sending user data to backend...');
        const userData = {
          id: result.user.uid,  // Using 'id' as expected by the backend
          email: result.user.email || '',
          display_name: result.user.displayName || '',
          photo_url: result.user.photoURL || ''
        };
        
        // console.log('User data being sent:', userData);
        
        await api.post('/users', userData, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        // User data sent to backend successfully
      } catch (backendError) {
        console.error('Error sending user data to backend:', backendError);
        // Continue anyway, as the authentication itself was successful
      }
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  }

  async function logout() {
    try {
      await signOut(auth);
      // Remove token on logout
      localStorage.removeItem('token');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }

  // Function to refresh the token periodically
  const refreshToken = async (user: User) => {
    try {
      console.log('Refreshing authentication token...');
      const token = await user.getIdToken(true); // Force refresh
      localStorage.setItem('token', token);
      console.log('Token refreshed and stored in localStorage');
      return token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  };

  useEffect(() => {
    console.log('Setting up auth state listener...');
    let refreshInterval: NodeJS.Timeout | null = null;
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? `User logged in: ${user.uid}` : 'User logged out');
      
      // Clear any existing refresh interval
      if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
      }
      
      if (user) {
        try {
          // Get and store the token when auth state changes
          const token = await user.getIdToken();
          localStorage.setItem('token', token);
          console.log('Token stored in localStorage, length:', token.length);
          
          // Set up token refresh interval (every 30 minutes)
          refreshInterval = setInterval(() => {
            refreshToken(user).then(newToken => {
              // console.log('Token refreshed on interval, new token length:', newToken ? newToken.length : 'failed');
            });
          }, 30 * 60 * 1000); // 30 minutes
        } catch (error) {
          console.error('Error getting ID token:', error);
        }
      } else {
        // Remove token when user signs out
        localStorage.removeItem('token');
        // console.log('Token removed from localStorage');
      }
      
      // Update the current user state
      setCurrentUser(user);
      setLoading(false);
    });

    // Clean up on unmount
    return () => {
      unsubscribe();
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem('token');
      if (token && !currentUser && !loading) {
        console.log('Token exists but no user is set, attempting to restore session...');
        try {
          // We can't directly restore the session with the token
          // Instead, we'll just clear the token if there's no user
          localStorage.removeItem('token');
          console.log('Removed potentially invalid token');
        } catch (error) {
          console.error('Error handling token:', error);
          localStorage.removeItem('token');
        }
      }
    };
    
    checkToken();
  }, [currentUser, loading]);

  const value: AuthContextType = {
    user: currentUser,
    loading,
    signInWithGoogle,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
